import { useEffect, useState, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import cn from 'classnames';
import { orderBy, compact } from 'lodash-es';
import { Page, TableList, PageEmptyState, PageErrorState, Searchbox, FilterPickerGroup, Button, PageContentHeader, Typography, MultiSelectFilterPicker, ConfirmationModal } from 'seagull';
import { Plus } from 'seagull/icons';
import { useStateFromUrl } from 'seagull/utils/useStateFromUrl';
import AccountantAccessLearnMoreButton from "../../components/AccountantAccessLearnMoreButton";
import { ManageContactModalForVendor } from "../../components/ManageContactModal";
import RestfulWrapper from "../../components/RestfulWrapper/RestfulWrapper";
import { routes } from "../../constants";
import { useEventReporter, useOverlayContext, useStore, useStoreActions } from "../../contexts";
import { useContacts } from "../../hooks/reactQuery/queries/useContacts";
import { useContactActions } from "../../hooks/useContactActions";
import { useDebouncedLogSearchToBI } from "../../hooks/useDebouncedLogSearchToBI";
import { useEnterAccountantAccess } from "../../hooks/useEnterAccountantAccess";
import { useInfiniteScroll } from "../../hooks/useInfiniteScroll";
import { useSetDocumentTitle } from "../../hooks/useSetDocumentTitle";
import { useSortFromUrl } from "../../hooks/useSortFromUrl";
import { ErrorResponse } from "../../infra/ErrorResponse";
import { handleRequest } from "../../infra/utils";
import { ContactStatus } from "../../models/ContactModel";
import { selectors } from "../../store/state-selectors";
import { Accounting } from "../../types/api.model";
import { CreateProposalButton } from "../../components/CreateProposalButton/CreateProposalButton";
import PageLoadingState from "../../components/PageLoadingState";
import styles from "./ContactsDashboard.module.scss";
import ContactsDashboardRow from "./ContactsDashboardRow";
import { EmptyStateIllustration } from "./EmptyStateIllustration";
import VendorAccountantAccessModal from "./VendorAccountantAccessModal";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const title = 'Contacts';
const headerItems = [{
  id: 'companyName',
  value: 'Company name'
}, {
  id: 'status',
  value: 'Status'
}, {
  id: 'primaryContactName',
  value: 'Contact name'
}, {
  id: 'contactDetails',
  value: 'Contact details'
}];
const sortIds = headerItems.map(i => i.id);
const columnDefs = [{
  width: [140, 300],
  sortable: true
},
// Company Name
{
  width: [140, 300],
  sortable: true
},
// Status
{
  width: [170, 170],
  sortable: true
},
// Contact name
{
  width: [160, 300],
  sortable: true
} // Contact details
];
const contactStatusFilterOptions = [{
  value: ContactStatus.Active,
  label: 'Active'
}, {
  value: ContactStatus.Archived,
  label: 'Archived'
}];
export const importContactsRoute = {
  id: 'import-contacts',
  text: 'Import contacts',
  url: routes.importContacts,
  shouldShow: _ref => {
    let {
      isVendor
    } = _ref;
    return isVendor;
  }
};
export function ActualContactsDashboard(props) {
  const {
    data: contacts,
    isRefreshing,
    refresh
  } = props;
  const qboCustomers = useStore(state => state.qboCustomers.data);
  const xeroCustomers = useStore(state => state.xeroCustomers.data);
  const {
    api,
    fetchAccountingCustomers,
    logBI,
    fetchIntegrationsSummary,
    fetchUserInfo
  } = useStoreActions();
  const {
    reportToGtm,
    reportToAppcues
  } = useEventReporter();
  const {
    startLoader,
    stopLoader,
    showErrorToast,
    showModal
  } = useOverlayContext();
  const defaultSort = useMemo(() => ({
    colIndex: 0,
    sort: 'desc'
  }), []);
  const {
    sort: tableSort,
    changeSort
  } = useSortFromUrl({
    defaultSort,
    sortIds
  });
  const scrollElRef = useRef(null);
  const debouncedLogSearchToBI = useDebouncedLogSearchToBI(11602);
  const {
    urlState: {
      search,
      status: contactStatusFilterValue
    },
    changeUrlState
  } = useStateFromUrl({
    search: {
      defaultValue: ''
    },
    status: {
      defaultValue: [ContactStatus.Active]
    }
  });
  const hasContacts = !!(contacts && contacts.length);
  const showQboIntegration = useStore(selectors.integration.showQboIntegration);
  const showXeroIntegration = useStore(selectors.integration.showXeroIntegration);
  const importContactsLink = useStore(selectors.filterAvailableRoutes([importContactsRoute]))[0];
  const enterAccountantAccess = useEnterAccountantAccess();
  const {
    withdrawToEditContactProposals
  } = useContactActions();
  const [registerSearchBoxHotkey, setRegisterSearchBoxHotkey] = useState(true);
  useEffect(() => {
    const promises = compact([showQboIntegration && fetchAccountingCustomers(Accounting.QBO), showXeroIntegration && fetchAccountingCustomers(Accounting.Xero), (showQboIntegration || showXeroIntegration) && fetchIntegrationsSummary()]);
    if (promises.length > 0) {
      Promise.all(promises);
    }
  }, [showQboIntegration, showXeroIntegration, fetchAccountingCustomers, fetchIntegrationsSummary]);
  const qboCustomerIdToDisplay = useMemo(() => {
    if (!qboCustomers) return {};
    return qboCustomers.reduce((prev, qboCustomer) => {
      prev[qboCustomer.id()] = qboCustomer.formattedDisplay();
      return prev;
    }, {});
  }, [qboCustomers]);
  const xeroCustomerIdToDisplay = useMemo(() => {
    if (!xeroCustomers) return {};
    return xeroCustomers.reduce((prev, xeroCustomer) => {
      prev[xeroCustomer.id()] = xeroCustomer.formattedDisplay();
      return prev;
    }, {});
  }, [xeroCustomers]);
  const filteredAndSorted = useMemo(() => {
    const filtered = (contacts || []).filter(contact => {
      // search
      const trimmedSearch = search ? search.trim().toLowerCase() : '';
      const termForSearch = "".concat(contact.companyName(), " ").concat(contact.firstName(), " ").concat(contact.lastName(), " ").concat(contact.email());
      const passesSearch = termForSearch.toLowerCase().includes(trimmedSearch);

      // status filter
      const passesStatusFilter = contactStatusFilterValue.includes(contact.status());
      return passesSearch && passesStatusFilter;
    });
    const {
      sort,
      colIndex
    } = tableSort || {
      colIndex: 0,
      sort: 'asc'
    };
    const sortPropertiesByHeaderIndex = [[c => c.companyName()], [s => s.status(), c => c.companyName()], [c => c.firstName(), c => c.lastName(), c => c.companyName()], [c => c.email(), c => c.companyName()]];
    const sortProperties = sortPropertiesByHeaderIndex[colIndex];
    const sortingOrder = sort === 'asc' ? 'desc' : 'asc';
    const orders = Array.from(Array(sortProperties.length)).map(() => sortingOrder);
    return orderBy(filtered, sortProperties, orders);
  }, [contacts, tableSort, search, contactStatusFilterValue]);
  const {
    paginatedData,
    onScroll
  } = useInfiniteScroll({
    data: filteredAndSorted,
    scrollElRef
  });
  const onSortChange = sort => {
    logBI(11603, {
      column: sort ? headerItems[sort.colIndex].id : 'none'
    });
    changeSort(sort);
  };
  const onClearSearch = () => {
    changeUrlState({
      search: ''
    });
    debouncedLogSearchToBI.onClear();
  };
  function onClearFilters() {
    changeUrlState({
      search: '',
      status: contactStatusFilterOptions.map(opt => opt.value)
    });
  }
  const openContactModal = async contact => {
    logBI(11600, {
      contact: contact && contact.toJSON()
    });
    reportToGtm('button-click', 'contacts-table-view-contact');
    reportToAppcues('button-click', 'contacts-table-view-contact');
    setRegisterSearchBoxHotkey(false);
    try {
      const savedContactData = await showModal({
        closeOnEscapeKey: true,
        closeOnClickOutside: true,
        content: _ref2 => {
          let {
            dismissModal
          } = _ref2;
          return /*#__PURE__*/_jsx(ManageContactModalForVendor, {
            "data-testid": "manage-contact-modal",
            existingContact: contact,
            onClose: dismissModal,
            origin: "Contacts dashboard"
          });
        }
      });
      if (savedContactData) {
        await refresh();
      }
    } finally {
      setRegisterSearchBoxHotkey(true);
    }
  };
  const onRestoreContact = async contact => {
    logBI(11605, {
      contact: {
        firstName: contact.firstName(),
        lastName: contact.lastName(),
        email: contact.email()
      }
    });
    const loader = 'restore-contact-loader';
    startLoader(loader);
    const result = await handleRequest(api.contact.restore(contact.id(), {
      version: contact.version()
    }));
    if (result.success) {
      await refresh();
    } else {
      showErrorToast(result.error.restoreContactErrorText(), 'restore-error-toast');
    }
    stopLoader(loader);
  };
  const onArchiveContact = async contact => {
    try {
      const shouldWithdrawProposals = contact.hasPendingProposals();
      const confirmed = await showModal({
        content: _ref3 => {
          let {
            dismissModal
          } = _ref3;
          return /*#__PURE__*/_jsx(ConfirmationModal, {
            skin: "danger",
            title: shouldWithdrawProposals ? 'This client has proposals awaiting approval' : 'Archive client',
            content: shouldWithdrawProposals ? "Archiving ".concat(contact.fullName(), " will automatically withdraw their pending proposals.") : "Are you sure you want to archive ".concat(contact.fullName(), "?"),
            primaryButtonLabel: shouldWithdrawProposals ? 'Archive client and withdraw proposal' : 'Archive client',
            secondaryButtonLabel: "Cancel",
            onPrimaryButtonClick: () => dismissModal(true),
            onSecondaryButtonClick: () => dismissModal(false),
            primaryButtonTestid: shouldWithdrawProposals ? 'confirm-withdraw-and-archive-btn' : undefined
          });
        }
      });
      if (confirmed) {
        logBI(11604, {
          contact: {
            firstName: contact.firstName(),
            lastName: contact.lastName(),
            email: contact.email()
          }
        });
        startLoader('archive-contact-loader');
        if (shouldWithdrawProposals) {
          await withdrawToEditContactProposals(contact.id());
        }
        await api.contact.archive(contact.id(), {
          version: contact.version()
        });
        await refresh();
      }
    } catch (err) {
      showErrorToast(new ErrorResponse(err).archiveContactErrorText(), 'archive-error-toast');
    } finally {
      stopLoader('archive-contact-loader');
    }
  };
  function onRequestAccountantAccess(contact) {
    showModal({
      closeOnClickOutside: true,
      closeOnEscapeKey: true,
      content: _ref4 => {
        let {
          dismissModal
        } = _ref4;
        return /*#__PURE__*/_jsx(VendorAccountantAccessModal, {
          dismissModal: dismissModal,
          primaryButtonLabel: "Request access",
          title: "Request Accountant Access",
          primaryButtonTestid: "request-acc-access-submit",
          content: /*#__PURE__*/_jsxs("div", {
            children: [/*#__PURE__*/_jsx(Typography, {
              className: styles.requestAccAccessParagraph,
              children: "With Accountant Access to your client's business, you can review their information and manage their payment methods."
            }), /*#__PURE__*/_jsx("ul", {
              className: cn(styles.requestAccAccessParagraph, styles.requestAccAccessList),
              children: ['Your client will receive an email about this request.', 'We will notify you by email when they accept your request.', 'Once approved by your client, you will be able to access their client portal using the Anchor logo next to their contact in your contacts list.'].map(text => /*#__PURE__*/_jsx("li", {
                children: /*#__PURE__*/_jsx(Typography, {
                  children: text
                })
              }, text))
            }), /*#__PURE__*/_jsx(AccountantAccessLearnMoreButton, {
              isVendor: true
            })]
          }),
          failureToastProps: {
            text: 'Failed to request Accountant Access',
            testid: 'acc-access-request-failure'
          },
          onSuccess: refresh,
          onSubmit: async () => {
            const customerId = contact.customerId();
            const companyName = contact.companyName();
            if (!customerId) return;
            logBI(13100, {
              customerId,
              companyName
            });
            await api.accountants.requestAccess(customerId);
          }
        });
      }
    });
  }
  function onCancelAccountantAccessRequest(contact) {
    showModal({
      closeOnClickOutside: true,
      closeOnEscapeKey: true,
      content: _ref5 => {
        let {
          dismissModal
        } = _ref5;
        return /*#__PURE__*/_jsx(VendorAccountantAccessModal, {
          dismissModal: dismissModal,
          skin: "danger",
          primaryButtonLabel: "Cancel request",
          title: "Cancel Accountant Access request",
          primaryButtonTestid: "cancel-acc-access-request-submit",
          content: /*#__PURE__*/_jsxs(Typography, {
            children: ["Are you sure you want to cancel your Accountant Access request to", ' ', /*#__PURE__*/_jsx(Typography, {
              as: "span",
              weight: "normal",
              children: contact.companyName()
            }), "?"]
          }),
          failureToastProps: {
            text: 'Failed to cancel Accountant Access request',
            testid: 'cancel-acc-access-request-failure'
          },
          onSuccess: refresh,
          onSubmit: async () => {
            const customerId = contact.customerId();
            const companyName = contact.companyName();
            if (!customerId) return;
            logBI(13103, {
              customerId,
              companyName
            });
            await api.accountants.cancelRequestAccess(customerId);
          }
        });
      }
    });
  }
  function onRemoveAccountantAccess(contact) {
    showModal({
      closeOnClickOutside: true,
      closeOnEscapeKey: true,
      content: _ref6 => {
        let {
          dismissModal
        } = _ref6;
        return /*#__PURE__*/_jsx(VendorAccountantAccessModal, {
          dismissModal: dismissModal,
          skin: "danger",
          primaryButtonLabel: "Remove access",
          title: "Remove Accountant Access",
          primaryButtonTestid: "remove-acc-access-submit",
          content: /*#__PURE__*/_jsxs("div", {
            children: [/*#__PURE__*/_jsxs(Typography, {
              children: ["Are you sure you want to remove your Accountant Access to", ' ', /*#__PURE__*/_jsx(Typography, {
                as: "span",
                weight: "normal",
                children: contact.companyName()
              }), "?"]
            }), /*#__PURE__*/_jsx(Typography, {
              children: "Once removed you will no longer be able to access their business."
            })]
          }),
          failureToastProps: {
            text: 'Failed to remove Accountant Access',
            testid: 'remove-acc-access-failure'
          }
          // We fetchUserInfo to remove the accountant workspace from userInfo.workspaces
          ,
          onSuccess: () => Promise.all([refresh(), fetchUserInfo()]),
          onSubmit: async () => {
            const customerId = contact.customerId();
            const companyName = contact.companyName();
            if (!customerId) return;
            logBI(13104, {
              customerId,
              companyName
            });
            await api.accountants.removeMe(customerId);
          }
        });
      }
    });
  }
  return /*#__PURE__*/_jsx(Page, {
    "data-testid": "contacts-dashboard",
    ref: scrollElRef,
    header: title,
    subheader: "View contact details",
    actions: /*#__PURE__*/_jsxs(_Fragment, {
      children: [importContactsLink && /*#__PURE__*/_jsx(Button, {
        skin: "primaryOutline",
        as: Link,
        to: importContactsLink.url,
        "data-testid": "goto-".concat(importContactsLink.id),
        children: importContactsLink.text
      }), /*#__PURE__*/_jsx(Button, {
        "data-testid": "create-contact-btn",
        prefixIcon: /*#__PURE__*/_jsx(Plus, {
          height: 18,
          width: 18
        }),
        onClick: () => openContactModal(),
        children: "Create contact"
      })]
    }),
    loading: isRefreshing,
    onScroll: onScroll,
    children: /*#__PURE__*/_jsx(TableList.Provider, {
      hasActionColumn: true,
      columnDefs: columnDefs,
      sort: tableSort,
      onSortChange: onSortChange,
      children: hasContacts ? /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsxs(PageContentHeader, {
          children: [/*#__PURE__*/_jsxs(FilterPickerGroup, {
            style: {
              marginBlockEnd: '24px'
            },
            children: [/*#__PURE__*/_jsx(Searchbox, {
              registerHotkey: registerSearchBoxHotkey,
              value: search,
              "data-testid": "contacts-dashboard-search",
              onChange: event => {
                const searchStr = event.target.value;
                changeUrlState({
                  search: searchStr
                });
                debouncedLogSearchToBI.onChange(searchStr);
              },
              onClear: onClearSearch
            }), /*#__PURE__*/_jsx(MultiSelectFilterPicker, {
              "data-testid": "contact-status-filter",
              value: contactStatusFilterValue,
              options: contactStatusFilterOptions,
              label: "Contact status",
              onChange: newFilterValue => {
                changeUrlState({
                  status: newFilterValue.length ? newFilterValue : contactStatusFilterOptions.map(opt => opt.value)
                });
              }
            })]
          }), /*#__PURE__*/_jsx(TableList.Header, {
            items: headerItems,
            "data-testid": "contacts-dashboard-table-header"
          })]
        }), filteredAndSorted.length ? paginatedData.map(contact => /*#__PURE__*/_jsx(ContactsDashboardRow, {
          contact: contact,
          isQboIntegrated: showQboIntegration,
          isXeroIntegrated: showXeroIntegration,
          qboCustomerIdToDisplay: qboCustomerIdToDisplay,
          xeroCustomerIdToDisplay: xeroCustomerIdToDisplay,
          openContactModal: openContactModal,
          onRequestAccountantAccess: onRequestAccountantAccess,
          onCancelAccountantAccessRequest: onCancelAccountantAccessRequest,
          onRemoveAccountantAccess: onRemoveAccountantAccess,
          onGotoClientPortal: c => enterAccountantAccess(c.customerId(), c.companyName()),
          onRestoreContact: onRestoreContact,
          onArchiveContact: onArchiveContact
        }, contact.id())) : /*#__PURE__*/_jsx(PageEmptyState, {
          illustration: /*#__PURE__*/_jsx(EmptyStateIllustration, {}),
          title: "No contacts match your request",
          message: "Try adjusting your filters",
          actions: /*#__PURE__*/_jsx(Button, {
            "data-testid": "contacts-dashboard-empty-search-clear-btn",
            onClick: onClearFilters,
            children: "Clear filters"
          })
        })]
      }) : /*#__PURE__*/_jsx(PageEmptyState, {
        "data-testid": "empty-state-page",
        illustration: /*#__PURE__*/_jsx(EmptyStateIllustration, {}),
        message: "No contacts yet. Here you will see your contact list.",
        actions: /*#__PURE__*/_jsx(CreateProposalButton, {
          "data-testid": "contacts-empty-state-new-btn",
          text: "Create a new agreement",
          hidePrefix: true
        })
      })
    })
  });
}
export function ContactsDashboard() {
  const {
    refresh: refreshContacts
  } = useContacts();
  useSetDocumentTitle(title);
  return /*#__PURE__*/_jsx(RestfulWrapper, {
    fetch: async () => {
      const {
        data
      } = await refreshContacts();
      return data;
    },
    renderChild: _ref7 => {
      let {
        data,
        loading,
        refresh
      } = _ref7;
      return /*#__PURE__*/_jsx(ActualContactsDashboard, {
        data: data,
        isRefreshing: loading,
        refresh: refresh
      });
    },
    renderError: _ref8 => {
      let {
        refresh
      } = _ref8;
      return /*#__PURE__*/_jsx(PageErrorState, {
        "data-testid": "error-state-page",
        title: title,
        onRefresh: refresh
      });
    },
    renderLoader: () => /*#__PURE__*/_jsx(PageLoadingState, {
      header: title
    })
  });
}